import type { Action, Reducer } from 'redux';
import { IAppThunkAction } from '.';
import ISplitJoinInfo from '../interfaces/ISplitJoinInfo';
import IPaymentGateway from '../interfaces/IPaymentGateway';
import SplitPaymentService from '../service/splitPayments';
import { PurchaseTypes, PURCHASE_TYPE_SELECTED } from './PurchaseTypes';
import { GET_PAYMENT_GATEWAYS_SUCCESS, GET_PAYMENT_GATEWAYS_COMPLETED } from './Payments';
import { FETCH_SHARE_SUCCESS } from './SplitPaymentLanding';
import { appInsights } from '../Logging/appInsights';

export type SplitJoinErrorType = 'notfound' | 'full' | 'expired' | 'generic' | undefined;

export interface ISplitJoinState {
    joinInfo: ISplitJoinInfo | undefined;
    loading: boolean;
    error: boolean;
    errorType: SplitJoinErrorType;
    submitting: boolean;
    submitError: boolean;
}

const FETCH_JOIN_INFO_REQUESTED: 'FETCH_JOIN_INFO_REQUESTED' = 'FETCH_JOIN_INFO_REQUESTED';
const FETCH_JOIN_INFO_SUCCESS: 'FETCH_JOIN_INFO_SUCCESS' = 'FETCH_JOIN_INFO_SUCCESS';
const FETCH_JOIN_INFO_FAILED: 'FETCH_JOIN_INFO_FAILED' = 'FETCH_JOIN_INFO_FAILED';

const JOIN_SPLIT_REQUESTED: 'JOIN_SPLIT_REQUESTED' = 'JOIN_SPLIT_REQUESTED';
const JOIN_SPLIT_SUCCESS: 'JOIN_SPLIT_SUCCESS' = 'JOIN_SPLIT_SUCCESS';
const JOIN_SPLIT_FAILED: 'JOIN_SPLIT_FAILED' = 'JOIN_SPLIT_FAILED';

interface IFetchJoinInfoRequestedAction extends Action {
    type: typeof FETCH_JOIN_INFO_REQUESTED;
}

interface IFetchJoinInfoSuccessAction extends Action {
    type: typeof FETCH_JOIN_INFO_SUCCESS;
    joinInfo: ISplitJoinInfo;
}

interface IFetchJoinInfoFailedAction extends Action {
    type: typeof FETCH_JOIN_INFO_FAILED;
    errorType: SplitJoinErrorType;
}

interface IJoinSplitRequestedAction extends Action {
    type: typeof JOIN_SPLIT_REQUESTED;
}

interface IJoinSplitSuccessAction extends Action {
    type: typeof JOIN_SPLIT_SUCCESS;
}

interface IJoinSplitFailedAction extends Action {
    type: typeof JOIN_SPLIT_FAILED;
}

export type KnownSplitJoinActions =
    | IFetchJoinInfoRequestedAction
    | IFetchJoinInfoSuccessAction
    | IFetchJoinInfoFailedAction
    | IJoinSplitRequestedAction
    | IJoinSplitSuccessAction
    | IJoinSplitFailedAction;

export const actionCreators = {
    fetchJoinInfo:
        (shareToken: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: FETCH_JOIN_INFO_REQUESTED });

            SplitPaymentService.GetJoinInfo(shareToken)
                .then((joinInfo) => {
                    if (joinInfo.availableSlots <= 0) {
                        dispatch({ type: FETCH_JOIN_INFO_FAILED, errorType: 'full' as SplitJoinErrorType });
                        return;
                    }

                    dispatch({ type: FETCH_JOIN_INFO_SUCCESS, joinInfo });

                    // Load payment gateways into the payments store
                    const gatewayData: IPaymentGateway[] = joinInfo.paymentGateways.map((gw) => ({
                        apiKey: gw.apiKey,
                        gateway: gw.gateway,
                        paymentMethodType: gw.paymentMethodType,
                    }));
                    dispatch({ type: GET_PAYMENT_GATEWAYS_SUCCESS, data: gatewayData });
                    dispatch({ type: GET_PAYMENT_GATEWAYS_COMPLETED });
                })
                .catch((error: Error) => {
                    const errorType: SplitJoinErrorType = error.name?.includes('404') ? 'notfound' : 'generic';
                    dispatch({ type: FETCH_JOIN_INFO_FAILED, errorType });
                    appInsights?.trackException(
                        { error: new Error('Error fetching split join info'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitJoin.ts', product: 'booking' }
                    );
                });
        },

    joinSplit:
        (shareToken: string, name: string, email: string, slotCount: number, culture?: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: JOIN_SPLIT_REQUESTED });

            SplitPaymentService.JoinSplit(shareToken, { name, email, slotCount, culture })
                .then((share) => {
                    dispatch({ type: JOIN_SPLIT_SUCCESS });

                    // Load the created share into splitPaymentLanding for the payment flow
                    dispatch({ type: FETCH_SHARE_SUCCESS, share });

                    // Set purchase type to SplitPayment so PaymentsSection works
                    dispatch({
                        type: PURCHASE_TYPE_SELECTED,
                        purchaseType: PurchaseTypes.SplitPayment,
                    });
                })
                .catch((error: Error) => {
                    dispatch({ type: JOIN_SPLIT_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error joining split payment'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitJoin.ts', product: 'booking' }
                    );
                });
        },
};

const unloadedState: ISplitJoinState = {
    joinInfo: undefined,
    loading: false,
    error: false,
    errorType: undefined,
    submitting: false,
    submitError: false,
};

export const reducer: Reducer<ISplitJoinState> = (
    state: ISplitJoinState | undefined,
    incomingAction: Action
): ISplitJoinState => {
    if (state === undefined) return unloadedState;

    const action = incomingAction as KnownSplitJoinActions;
    switch (action.type) {
        case FETCH_JOIN_INFO_REQUESTED:
            return {
                ...state,
                loading: true,
                error: false,
                errorType: undefined,
            };
        case FETCH_JOIN_INFO_SUCCESS:
            return {
                ...state,
                loading: false,
                joinInfo: action.joinInfo,
                error: false,
                errorType: undefined,
            };
        case FETCH_JOIN_INFO_FAILED:
            return {
                ...state,
                loading: false,
                error: true,
                errorType: action.errorType,
            };
        case JOIN_SPLIT_REQUESTED:
            return {
                ...state,
                submitting: true,
                submitError: false,
            };
        case JOIN_SPLIT_SUCCESS:
            return {
                ...state,
                submitting: false,
                submitError: false,
            };
        case JOIN_SPLIT_FAILED:
            return {
                ...state,
                submitting: false,
                submitError: true,
            };
        default:
            return state;
    }
};
